'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
export interface LiveValueSample {
  readonly index: number;
  readonly value: number;
}
interface TrajectoryPoint {
  time: number;
  value: number;
}
interface TrajectorySnapshot {
  readonly resetKey: string;
  readonly pointsByIndex: Record<number, ReadonlyArray<TrajectoryPoint>>;
}
interface TrajectoryStore {
  getSnapshot: () => TrajectorySnapshot;
  subscribe: (listener: () => void) => () => void;
  update: (args: TrajectoryUpdateArgs) => void;
}
interface TrajectoryUpdateArgs {
  samples: ReadonlyArray<LiveValueSample>;
  windowSecs: number;
  resetKey: string;
}
const EMPTY_POINTS: Record<number, ReadonlyArray<TrajectoryPoint>> = {};
const LIVE_SAMPLE_INTERVAL_MS = 5_000;
const BOOTSTRAP_REPLACEMENT_SECONDS = 1;
function createTrajectoryStore(): TrajectoryStore {
  let snapshot: TrajectorySnapshot = {
    resetKey: '',
    pointsByIndex: EMPTY_POINTS
  };
  let buffers = new Map<number, TrajectoryPoint[]>();
  let previousValues = new Map<number, number>();
  let bootstrapCandidates = new Set<number>();
  const listeners = new Set<() => void>();
  return {
    getSnapshot: (): TrajectorySnapshot => snapshot,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
    update({
      samples,
      windowSecs,
      resetKey
    }: TrajectoryUpdateArgs): void {
      let changed = snapshot.resetKey !== resetKey;
      if (changed) {
        buffers = new Map();
        previousValues = new Map();
        bootstrapCandidates = new Set();
      }
      const now = Date.now() / 1000;
      const cutoff = now - windowSecs;
      const activeIndices = new Set(samples.map(sample => sample.index));
      for (const index of previousValues.keys()) {
        if (!activeIndices.has(index)) {
          previousValues.delete(index);
          bootstrapCandidates.delete(index);
        }
      }
      for (const sample of samples) {
        if (!Number.isFinite(sample.value)) continue;
        const points = buffers.get(sample.index) ?? [];
        const previousValue = previousValues.get(sample.index);
        previousValues.set(sample.index, sample.value);
        const last = points[points.length - 1];
        const isBootstrapCorrection = last !== undefined && bootstrapCandidates.has(sample.index) && points.length === 1 && previousValue !== undefined && previousValue !== sample.value && now - last.time < BOOTSTRAP_REPLACEMENT_SECONDS;
        if (last === undefined) {
          points.push({
            time: now,
            value: sample.value
          });
          bootstrapCandidates.add(sample.index);
          changed = true;
        } else if (isBootstrapCorrection) {
          last.time = now;
          last.value = sample.value;
          bootstrapCandidates.delete(sample.index);
          changed = true;
        } else if (previousValue !== sample.value || now - last.time >= LIVE_SAMPLE_INTERVAL_MS / 1_000) {
          if (last.time === now) last.value = sample.value;else points.push({
            time: now,
            value: sample.value
          });
          bootstrapCandidates.delete(sample.index);
          changed = true;
        }
        while (points.length > 1 && (points[1]?.time ?? now) < cutoff) {
          points.shift();
          changed = true;
        }
        if (points.length > 0) buffers.set(sample.index, points);
      }
      if (!changed) return;
      const pointsByIndex: Record<number, ReadonlyArray<TrajectoryPoint>> = {};
      for (const [index, points] of buffers) pointsByIndex[index] = points.slice();
      snapshot = {
        resetKey,
        pointsByIndex
      };
      for (const listener of listeners) listener();
    }
  };
}

/**
 * Accrues current per-outcome values into a rolling live-chart window.
 *
 * The hook is intentionally independent of the visible chart mode. Consumers
 * can keep multiple trajectories warm while Live is selected, then switch tabs
 * without starting that tab's history from an empty buffer.
 */
export function useLiveValueTrajectory(args) {
  const $ = _c(13);
  const {
    samples,
    enabled,
    windowSecs,
    resetKey
  } = args;
  const [store] = useState(createTrajectoryStore);
  const latestSamples = useRef(samples);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== resetKey || $[2] !== samples || $[3] !== store || $[4] !== windowSecs) {
    t0 = () => {
      latestSamples.current = samples;
      if (enabled && samples !== undefined) {
        store.update({
          samples,
          windowSecs,
          resetKey
        });
      }
    };
    t1 = [samples, enabled, windowSecs, resetKey, store];
    $[0] = enabled;
    $[1] = resetKey;
    $[2] = samples;
    $[3] = store;
    $[4] = windowSecs;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[7] !== enabled || $[8] !== resetKey || $[9] !== store || $[10] !== windowSecs) {
    t2 = () => {
      if (!enabled) {
        return;
      }
      const intervalId = setInterval(() => {
        const currentSamples = latestSamples.current;
        if (currentSamples !== undefined) {
          store.update({
            samples: currentSamples,
            windowSecs,
            resetKey
          });
        }
      }, LIVE_SAMPLE_INTERVAL_MS);
      return () => {
        clearInterval(intervalId);
      };
    };
    t3 = [enabled, windowSecs, resetKey, store];
    $[7] = enabled;
    $[8] = resetKey;
    $[9] = store;
    $[10] = windowSecs;
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  useEffect(t2, t3);
  if (!enabled || snapshot.resetKey !== resetKey) {
    return EMPTY_POINTS;
  }
  return snapshot.pointsByIndex;
}